import type { Countries } from '../i18n/i18n.language-config';
import type { ProductType } from '../types';
import type { PolicyStatusOptions } from '../types/common/misc';
import type { Nullable } from '../types/common/utility';
import type { FirmographicDataDto } from '../types/firmo/firmographic-data.dto';
import type { BasePolicyDto } from '../types/policy/policy.dto';
import type { QuoteFieldValue, RaterResponse } from './quotes.api.types';
import type { ValueConfig } from '../console/workflows/quotes/prime-x/api/types/rater-x-preset';
import type { SubmissionFirmographics } from './submissions/submissions.api.types';

export interface GetPrimeXPolicyDetails {
  policy: {
    agencyName: string;
    agentFirstName: string;
    agentLastName: string;
    waitingPeriod: number;
    agentEmail: string;
    policyNumber: string;
    quoteNumber?: string;
    customerFirstName: string;
    customerLastName: string;
    customerEmail: string;
    boundOn: string;
    binderExpiresOn: string;
    effectiveDate: string;
    endDate: string;
    totalCoverages: number;
    totalEndorsements: number;
    limit: number;
    deductible: number;
    totalPremium: number;
    product: ProductType;
  };
  firmographicData: {
    name: string;
    country: Countries;
    agencyBilled: boolean;
    numberOfConnectors: number;
    dbaOrTradestyle: string;
    naicsCode: number;
    sicCode?: string;
    revenue: number;
    dunsNumber: string;
    domainName: string;
    domains: string[];
    url: string;
    yearEstablished: number;
    entityType: string;
    isFranchise: boolean;
    primaryIndustry: string;
    secondaryIndustry: string;
    ownershipType: string;
    noOfEmployeesAll: number;
    address1: string;
    city: string;
    state: string;
    zipCode: string;
    phoneNumber: string;
    p100ProQuestions: {
      [key: string]: {
        answer: boolean | string | string[] | null;
        comments: string | null;
      };
    };
  };
  cowbellRiskFactors: CowbellRiskFactors;
  factors: { [key: string]: number };
  /**
   * ISO 4217 code for this policy's premium/limit/deductible amounts, resolved server-side by
   * `ProductCurrencyResolver` (MS-270) on the same `GET /api/policy/v1/:productType/:policyId`
   * route `ESPolicyPx.currency` documents — this type just covers the `view=full` shape that
   * route also serves. May be absent on older/edge-case payload shapes — never infer one from
   * product/region.
   */
  currency?: string;
}

type CowbellRiskFactors = {
  companyAggregateCF: number;
  industryAggregateCF: number;
  factors: {
    networkSecurityCF: number;
    cloudSecurityCF: number;
    endpointSecurityCF: number;
    fundsTransferCF: number;
    complianceCF: number;
    darkIntelligenceCF: number;
    cyberExtortionCF: number;
    insiderThreatCF: number;
    supplyChainCF: number;
  };
};

export interface GetPrimeXPolicyDetailsByViewResponse {
  policy: Partial<BasePolicyDto>;
  firmographicData: FirmographicDataDto;
  dynamicFields?: QuoteFieldValue[];
  globalValue?: ValueConfig;
  componentsGlobalFieldValues?: Record<string, ValueConfig>;
  componentsDynamicFields?: Record<string, QuoteFieldValue[]>;
  cowbellRiskFactors?: {
    companyAggregateCF: number;
    industryAggregateCF: number;
    factors: {
      cloudSecurityCF: number;
      complianceCF: number;
      cyberExtortionCF: number;
      darkIntelligenceCF: number;
      endpointSecurityCF: number;
      fundsTransferCF: number;
      insiderThreatCF: number;
      networkSecurityCF: number;
      supplyChainCF: number;
    };
  };
  totalCoverages?: number;
  totalEndorsements?: number;
  refund?: unknown;
  raterResponse: RaterResponse;
  /**
   * ISO 4217 code for this policy's premium/limit/deductible amounts, resolved server-side by
   * `ProductCurrencyResolver` (MS-270) and returned on `GET /api/policy/v1/:productType/:policyId`
   * — the same endpoint/response `ESPolicyPx.currency` documents. May be absent on older/edge-case
   * payload shapes — never infer one from product/region.
   */
  currency?: string;
}

// Response shape of `/api/policy/v1/P250/details/:policyId` — a flat legacy
// policy DTO (fields directly on the root), unlike Prime X's
// `{ policy, firmographicData }` envelope above. Reuses BasePolicyDto
export type P250PolicyPageDetailsResponse = Partial<BasePolicyDto> & {
  firmographicData?: Pick<
    FirmographicDataDto,
    'naicsCode' | 'primaryIndustry' | 'secondaryIndustry' | 'state'
  >;
  /**
   * ISO 4217 code, resolved server-side by `ProductCurrencyResolver` (MS-270) and returned on
   * `GET /api/policy/v1/P250/:policyId` (`PolicyDetailsResponseBase250`). May be absent on
   * older/edge-case payload shapes — never infer one from product/region.
   */
  currency?: string;
};

export type PolicyPg = {
  id: string;
  dynamicFields?: Map<string, unknown>;
  raterResponse: RaterResponse;
  firmographics: SubmissionFirmographics;
  risk: Risk;
  quoteId: string;
  policyNumber: string;
  status: PolicyStatusOptions;
  product: ProductType;
  issuedOn: string;
  boundOn: string;
  binderExpiresOn: string;
  effectiveDate: string;
  endDate: string;
  created: string;
  modified: string;
  limit: number;
  deductible: number;
  waitingPeriod: number;
  agency: Agency;
  agent: Agent;
  retroactive: {
    date?: string;
    period?: number;
  };
  basePremium: number;
  premium: number;
  totalPremium: number;
  commissionAmount: number;
  isRaterXCompatible?: boolean;
};

type Risk = {
  companyAcf: Nullable<number>;
  industryAcf: Nullable<number>;
  networkSecurity: Nullable<number>;
  cloudSecurity: Nullable<number>;
  endpointSecurity: Nullable<number>;
  darkIntelligence: Nullable<number>;
  fundsTransfer: Nullable<number>;
  cyberExtortion: Nullable<number>;
  supplyChain: Nullable<number>;
  compliance: Nullable<number>;
  insiderThreat: Nullable<number>;
  industryNetworkSecurity: IndustryCowbellFactors;
  industryCloudSecurity: IndustryCowbellFactors;
  industryEndpointSecurity: IndustryCowbellFactors;
  industryDarkIntelligenceSecurity: IndustryCowbellFactors;
  industryFundsTransferSecurity: IndustryCowbellFactors;
  industryCyberExtortionSecurity: IndustryCowbellFactors;
  industrySupplyChainSecurity: IndustryCowbellFactors;
  industryComplianceSecurity: IndustryCowbellFactors;
  industryInsiderThreatSecurity: IndustryCowbellFactors;
};

export interface IndustryCowbellFactors {
  score: number;
  min?: number;
  max?: number;
}
type Agent = {
  id: string;
  agentContact: {
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
  };
  team: { teamId: string; teamName: string };
};

type Agency = {
  id: string;
  agencyName: Nullable<string>;
};

export function isPolicyPg(policy: unknown): policy is PolicyPg {
  return (
    typeof policy === 'object' &&
    policy != null &&
    'dynamicFields' in policy &&
    'raterResponse' in policy &&
    'risk' in policy &&
    'firmographics' in policy
  );
}

export type ESPolicyPx = {
  policy: Partial<BasePolicyDto> & { revisionData?: PolicyRevision };
  firmographicData: FirmographicDataDto;
  dynamicFields: QuoteFieldValue[];
  globalValue?: ValueConfig;
  componentsGlobalFieldValues: unknown;
  componentsDynamicFields: Record<string, QuoteFieldValue[]>;
  totalCoverages?: number;
  totalEndorsements?: number;
  refund?: unknown;
  cowbellRiskFactors: CowbellRiskFactors;
  revisionData: PolicyRevision;
  /**
   * ISO 4217 code for this policy's premium/limit/deductible amounts, resolved server-side by
   * `ProductCurrencyResolver` (MS-270) and returned on both
   * `GET /api/policy/v1/:productType/:policyId` and `GET /api/policy/v1/revision/:revisionId`.
   * May be absent on older/edge-case payload shapes — never infer one from product/region.
   */
  currency?: string;
};

export function isEsPolicyPx(policy: unknown): policy is ESPolicyPx {
  return (
    typeof policy === 'object' &&
    policy != null &&
    'dynamicFields' in policy &&
    'policy' in policy &&
    'firmographicData' in policy
  );
}

export type Diff<T> = {
  oldValue?: T;
  newValue?: T;
  diffType?: DiffType;
  diffs?: {
    [K in keyof T]?: Diff<T[K]>;
  };
};

export enum DiffType {
  ADDED = 'ADDED',
  REMOVED = 'REMOVED',
  CHANGED = 'CHANGED',
}

export function isPolicyRevision(rev: unknown): rev is PolicyRevision[] {
  if (!Array.isArray(rev) || !rev.length) {
    return false;
  }

  return (
    rev[0].revisionId != null &&
    rev[0].revisionEffectiveDate != null &&
    rev[0].revisionNumber != null &&
    rev[0].revisionType != null
  );
}

export type PolicyRevisionResp = {
  revisions: PolicyRevision[];
};

export type PolicyRevision = {
  revisionEffectiveDate: string;
  revisionId: string;
  revisionNumber: string;
  revisionType: RevisionType;
  active: boolean;
  createdBy: string;
  created: string;
  revisionDocumentId?: string;
  isEffective: boolean;
  generatedBy: GeneratedBy;
  revertReason?: string;
};

export enum GeneratedBy {
  USER = 'USER',
  SYSTEM = 'SYSTEM',
}

export enum RevisionType {
  MTE = 'MTE',
  BASE = 'BASE',
  BINDER = 'BINDER',
  MTC = 'MTC',
  VOID = 'VOID',
  REVERT = 'REVERT',
  REINSTATE = 'REINSTATE',
  BOR_CHANGE = 'BOR_CHANGE',
  AGENT_UPDATE = 'AGENT_UPDATE',
  BINDER_RESCIND = 'BINDER_RESCIND',
  TAIL_COVERAGE = 'TAIL_COVERAGE',
}

export interface CreateBorChangeRequestDto {
  policyNumber: string;
  docId?: string;
  agencyId: string;
  agencyName: string;
  agentContact: AgentContactDto;
  team: TeamDto;
}

export interface AgentContactDto {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber?: string;
}

export interface TeamDto {
  id: string;
  name: string;
}
